/* eslint-disable react/style-prop-object */
import "./MowerDialog.scss";

import { Box, OverflowContainer } from "@husqvarna/ui-core";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import debounce from "lodash/debounce";
import MowerAreaSelector from "./Components/MowerAreaSelector/MowerAreaSelector";
import MowerDialogStatusBar from "./Components/MowerDialogStatusBar/MowerDialogStatusBar";
import MowerDialogHeader from "./Components/MowerDialogHeader";
import UpdateProposalProductModel from "../../../models/UpdateProposalProductModel";
import Project from "../../../models/Project";
import { Area } from "../../../models/Area";
import {
  useSaveProposalProductMutation,
} from "../../../projectApi";
import {
  calculateAreaCoverage,
  calculateRemainingMowerCapacity,
  mowerCanHandleAreaCuttingQualities,
  mowerCanHandleAreaSlope,
  getAreaSizeCoveredByOtherProducts,
} from "../../../coverageCalculations";
import { setAreaCoverageValues } from "../../../../shared/store/equipmentEditSlice";
import { ProposalProduct } from "../../../models/ProposalProduct";
import { Product } from "../../../models/Product";
import { getColorClassForColor, getNextAvailableColor } from "../../../../shared/constants/proposalProductColors";
import { isArraysEqual } from "../../../../shared/utils";

export type MowerDialogProps = {
  mower: Product;
  proposalProduct?: ProposalProduct;
  project: Project;
  onCancel?: () => void;
};

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 20;

const getAllUsedColorsForProposalProductsInProject = (project: Project): string[] => {
  return project.proposal?.mowers
    ?.map(mower => mower.color)
    .filter((color): color is string => !!color) ?? [];
};

const getColorForProposalProduct = (
  proposalProduct: ProposalProduct | undefined,
  project: Project
): string => {
  if (proposalProduct?.color) {
    return proposalProduct.color;
  }
  const usedColors = getAllUsedColorsForProposalProductsInProject(project);
  return getNextAvailableColor(usedColors);
};

const getInitialCuttingHours = (
  proposalProduct: ProposalProduct | undefined,
  project: Project
): boolean => {
  if (!proposalProduct) return true;
  const connections = project.areas
    .flatMap(x => x.proposalProductConnections)
    .filter(x => proposalProduct.areaProposalConnectionIds?.includes(x.id));
  return connections[0]?.possibleToMowOnDifferentHours ?? true;
};

const MowerDialog = ({ mower, proposalProduct, project, onCancel }: MowerDialogProps) => {
  const dispatch = useDispatch();
  const [currentProposalProduct, setCurrentProposalProduct] = useState<ProposalProduct | undefined>(proposalProduct);
  const [saveProposalProduct] = useSaveProposalProductMutation();
  const isInitializedRef = useRef(false);
  const proposalProductId = useRef<string>(
    proposalProduct?.id ?? crypto.randomUUID()
  );

  // ============================================================================
  // FROZEN DATA INITIALIZATION
  // Freeze all data when dialog opens to prevent UI updates from backend reloads
  // ============================================================================
  const frozenDataRef = useRef<{
    areas: Area[];
    proposalProduct?: ProposalProduct;
    initialQuantity: number;
    initialAreaIds: string[];
    initialCuttingHours: boolean;
    initialColor: string;
  } | null>(null);

  if (frozenDataRef.current === null) {
    frozenDataRef.current = {
      areas: project.areas.map(area => ({
        ...area,
        coveragePercent: area.coveragePercent,
        proposalProductConnections: area.proposalProductConnections
          ? area.proposalProductConnections.map(conn => ({ ...conn }))
          : [],
      })),
      proposalProduct: proposalProduct ? { ...proposalProduct } : undefined,
      initialQuantity: proposalProduct?.quantity ?? 1,
      initialAreaIds: proposalProduct?.areaIds ?? [],
      initialColor: getColorForProposalProduct(proposalProduct, project),
      initialCuttingHours: getInitialCuttingHours(proposalProduct, project),
    };
  }

  const frozenAreas = frozenDataRef.current.areas;
  const getFrozenProposalProduct = () => frozenDataRef.current?.proposalProduct;
  const frozenProposalProduct = getFrozenProposalProduct();

  const updateFrozenProposalProduct = (product: ProposalProduct | undefined) => {
    if (frozenDataRef.current) {
      frozenDataRef.current.proposalProduct = product;
      // Update initial values to reflect the saved state
      if (product) {
        frozenDataRef.current.initialAreaIds = product.areaIds ?? [];
        frozenDataRef.current.initialQuantity = product.quantity ?? 1;
        frozenDataRef.current.initialCuttingHours = getInitialCuttingHours(product, project);
      } else {
        frozenDataRef.current.initialAreaIds = [];
        frozenDataRef.current.initialQuantity = 1;
        frozenDataRef.current.initialCuttingHours = true;
      }
    }
  };

  // ============================================================================
  // FORM SETUP
  // ============================================================================
  const form = useForm({
    defaultValues: {
      quantity: frozenDataRef.current.initialQuantity,
      areaIds: frozenDataRef.current.initialAreaIds,
      possibleToMowOnDifferentHours: frozenDataRef.current.initialCuttingHours,
    },
  });

  const quantity = form.watch("quantity");
  const areaIds = form.watch("areaIds");
  const possibleToMowOnDifferentHours = form.watch("possibleToMowOnDifferentHours");
  const currentQuantity = quantity || 1;

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const selectedAreas = useMemo(
    () => frozenAreas.filter((area) => areaIds?.includes(area.id)),
    [frozenAreas, areaIds]
  );

  const validation = useMemo(() => {
    const capacity = calculateRemainingMowerCapacity(
      mower,
      currentQuantity,
      selectedAreas,
      possibleToMowOnDifferentHours ?? true,
      frozenProposalProduct
    );

    return {
      capacityPercent: capacity,
      capacityError: capacity < 0,
      cuttingQualityError:
        selectedAreas.length > 0 &&
        !mowerCanHandleAreaCuttingQualities(mower, selectedAreas),
    };
  }, [currentQuantity, possibleToMowOnDifferentHours, selectedAreas, mower, frozenProposalProduct]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  const toggleArea = useCallback(
    (area: Area) => {
      const currentAreas = areaIds || [];
      const newAreas = currentAreas.includes(area.id)
        ? currentAreas.filter((id: string) => id !== area.id)
        : [...currentAreas, area.id];
      form.setValue("areaIds", newAreas);
    },
    [areaIds, form]
  );

  const changeQuantity = useCallback(
    (delta: number) => {
      const newQuantity = Math.max(MIN_QUANTITY, Math.min(MAX_QUANTITY, currentQuantity + delta));
      form.setValue("quantity", newQuantity);
    },
    [currentQuantity, form]
  );

  const toggleCuttingHours = useCallback(
    (checked: boolean) => {
      form.setValue("possibleToMowOnDifferentHours", checked);
    },
    [form]
  );

  const isAreaDisabled = useCallback(
    (area: Area) => {
      const isSelected = selectedAreas.some((a) => a.id === area.id);
      return !isSelected && !mowerCanHandleAreaSlope(mower, area);
    },
    [selectedAreas, mower]
  );

  const showCapacityError = useCallback(
    (area: Area) => selectedAreas.includes(area) && validation.capacityError,
    [selectedAreas, validation.capacityError]
  );

  const showQualityError = useCallback(
    (area: Area) => selectedAreas.includes(area) && validation.cuttingQualityError,
    [selectedAreas, validation.cuttingQualityError]
  );

  const showSlopeError = useCallback(
    (area: Area) => !mowerCanHandleAreaSlope(mower, area),
    [mower]
  );

  const handleBackClick = () => {
    debouncedSaveRef.current.cancel();
    onCancel?.();
  };

  // ============================================================================
  // AREA COVERAGE CALCULATION
  // ============================================================================
  const calculateAllAreaCoverage = useCallback(() => {
    const previousAreas = frozenProposalProduct
      ? frozenAreas.filter((a) => frozenProposalProduct.areaIds.includes(a.id))
      : undefined;

    const coverage = calculateAreaCoverage(
      mower,
      currentQuantity,
      selectedAreas,
      frozenProposalProduct,
      previousAreas
    );

    const allCoverageValues: Record<string, number> = { ...coverage };

    frozenAreas.forEach((area) => {
      if (allCoverageValues[area.id] !== undefined) return;

      let coveragePercent = 0;
      if (frozenProposalProduct) {
        const coveredSize = getAreaSizeCoveredByOtherProducts(area, frozenProposalProduct);
        coveragePercent = Math.round((coveredSize / area.size) * 100);
      } else if (area.proposalProductConnections?.length) {
        const totalCoveredSize = area.proposalProductConnections.reduce(
          (sum, conn) => sum + (conn.areaSizeCoveredByProduct || 0),
          0
        );
        coveragePercent = Math.round((totalCoveredSize / area.size) * 100);
      }

      allCoverageValues[area.id] = Math.min(coveragePercent, 100);
    });

    return allCoverageValues;
  }, [currentQuantity, selectedAreas, mower, frozenAreas, frozenProposalProduct]);

  useEffect(() => {
    const allCoverageValues = calculateAllAreaCoverage();
    dispatch(setAreaCoverageValues(allCoverageValues));
  }, [calculateAllAreaCoverage, dispatch]);

  // ============================================================================
  // AUTO-SAVE LOGIC
  // ============================================================================
  const isSavingRef = useRef(false);
  const needsResaveRef = useRef(false);

  const hasFormValuesChanged = (): boolean => {
    const frozenData = frozenDataRef.current;
    if (!frozenData) return true;

    const currentAreas = form.getValues("areaIds") || [];
    const currentQty = form.getValues("quantity") || 1;
    const currentHours = form.getValues("possibleToMowOnDifferentHours") ?? true;

    return currentQty !== frozenData.initialQuantity
      || !isArraysEqual(currentAreas, frozenData.initialAreaIds)
      || currentHours !== frozenData.initialCuttingHours;
  };

  const debouncedSaveRef = useRef(
    debounce(
      async (
        mutation: typeof saveProposalProduct,
        updateCurrentProposal: (product: ProposalProduct | undefined) => void,
        updateFrozen: (product: ProposalProduct | undefined) => void
      ) => {
        if (isSavingRef.current) {
          needsResaveRef.current = true;
          return;
        }

        if (!hasFormValuesChanged()) return;

        const currentFrozen = frozenDataRef.current?.proposalProduct;
        const currentAreaIds = form.getValues("areaIds") || [];

        if (currentAreaIds.length === 0 && !currentFrozen) return;

        const data: UpdateProposalProductModel = {
          proposalProductId: proposalProductId.current,
          projectId: project.id,
          productId: mower.id,
          quantity: form.getValues("quantity") || 1,
          areaIds: currentAreaIds,
          possibleToMowOnDifferentHours: form.getValues("possibleToMowOnDifferentHours") ?? true,
          color: getColorForProposalProduct(currentFrozen, project),
        };

        isSavingRef.current = true;
        try {
          const result = await mutation(data).unwrap();

          if (!result || (!data.areaIds || data.areaIds.length === 0)) {
            updateCurrentProposal(undefined);
            updateFrozen(undefined);
          } else {
            updateCurrentProposal(result);
            updateFrozen(result);
          }
        } catch (err) {
          console.error("Save failed:", err);
        } finally {
          isSavingRef.current = false;
          if (needsResaveRef.current) {
            needsResaveRef.current = false;
            debouncedSaveRef.current(mutation, updateCurrentProposal, updateFrozen);
          }
        }
      },
      500
    )
  );

  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    debouncedSaveRef.current(
      saveProposalProduct,
      setCurrentProposalProduct,
      updateFrozenProposalProduct
    );
  }, [quantity, areaIds, possibleToMowOnDifferentHours]);

  useEffect(() => {
    return () => debouncedSaveRef.current.cancel();
  }, []);

  // ============================================================================
  // SYNC STATE WITH PROPS
  // ============================================================================
  useEffect(() => {
    setCurrentProposalProduct(proposalProduct);
  }, [proposalProduct]);


  const frozenProposalProductColor = frozenProposalProduct?.color ?? frozenDataRef.current?.initialColor;

  return (
    <Box height="full" container>
      <Box height="full" flexDirection="column">
        <MowerDialogHeader
          capacityPercent={validation.capacityPercent}
          mower={mower}
          mowerQuantity={currentQuantity}
          unitSystem={project.region.unitSystem}
          onBackClick={handleBackClick}
          form={form}
          changeQuantity={changeQuantity}
          toggleCuttingHours={toggleCuttingHours}
          possibleToMowOnDifferentHours={possibleToMowOnDifferentHours ?? true}
          minQuantity={MIN_QUANTITY}
          maxQuantity={MAX_QUANTITY}
          currentQuantity={currentQuantity}
          colorHex={frozenProposalProductColor}
        />

        <Box container noOverflow flexGrow={1}>
          <OverflowContainer direction="vertical" py={2} ml={4} pr={2} mr={2}>
            <MowerAreaSelector
              selectedAreas={selectedAreas}
              onChange={toggleArea}
              isDisabled={isAreaDisabled}
              showCapacityError={showCapacityError}
              showQualityError={showQualityError}
              showSlopeError={showSlopeError}
              unitSystem={project.region.unitSystem}
              savedAreas={frozenProposalProduct?.areaIds}
              mower={mower}
              proposalProduct={frozenProposalProduct}
              areas={frozenAreas}
            />
          </OverflowContainer>
        </Box>

        <MowerDialogStatusBar
          quantity={quantity}
          selectedAreasCount={selectedAreas.length}
          hasCuttingQualityError={validation.cuttingQualityError}
          hasCapacityError={validation.capacityError}
        />
      </Box>
    </Box>
  );
};

export default MowerDialog;
